import { Check, Filter, X } from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

// Inline column filter — renders a small funnel icon inside a column
// header. Click → popover (Radix dropdown content reused as a popover) with
// either a multi-select of distinct values or a date range, depending on
// which variant the caller mounts. The icon fills when at least one value
// is selected so users can see at a glance which columns are scoped.

// Visual-only chip rendered inside the dropdown trigger button — the outer
// <button> (DropdownMenuTrigger asChild) owns role + a11y; this is just a
// styled icon container that reacts to whether the filter is active.
function FilterIconChip({ active }: { active: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-flex size-5 items-center justify-center rounded transition-colors",
        active
          ? "bg-primary/15 text-primary group-hover:bg-primary/25"
          : "text-muted-foreground/60 group-hover:bg-muted group-hover:text-foreground",
      )}
    >
      <Filter className={cn("size-3", active && "fill-current")} />
    </span>
  );
}

// Multi-select variant — caller passes a list of options (typically the
// distinct values pulled off the loaded rows) and the currently selected
// subset. Empty array = no filter applied. The popover is searchable for
// long lists (states, adjusters).
type MultiSelectColumnFilterProps = {
  ariaLabel: string;
  options: string[];
  value: string[];
  onChange: (next: string[]) => void;
  searchable?: boolean;
};

export function MultiSelectColumnFilter({
  ariaLabel,
  options,
  value,
  onChange,
  searchable = false,
}: MultiSelectColumnFilterProps) {
  const [query, setQuery] = useState("");
  const selected = useMemo(() => new Set(value), [value]);
  const visible = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  function toggle(opt: string) {
    if (selected.has(opt)) {
      onChange(value.filter((v) => v !== opt));
    } else {
      onChange([...value, opt]);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={value.length > 0 ? `${ariaLabel}: ${value.length} selected` : ariaLabel}
          aria-pressed={value.length > 0}
          className="ml-1 inline-flex size-5 items-center justify-center rounded transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <FilterIconChip active={value.length > 0} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={4}
        className="w-56 p-2"
        onClick={(e) => e.stopPropagation()}
      >
        {searchable && (
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search..."
            className="mb-2 h-7 text-xs"
          />
        )}
        <div className="max-h-56 overflow-y-auto">
          {visible.length === 0 ? (
            <div className="px-2 py-1.5 text-muted-foreground text-xs">No matches</div>
          ) : (
            visible.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
              >
                <span className="truncate">{opt || "(blank)"}</span>
                {selected.has(opt) && <Check className="size-3.5 shrink-0 text-primary" />}
              </button>
            ))
          )}
        </div>
        {value.length > 0 && (
          <>
            <div className="-mx-2 my-1 h-px bg-border" />
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-full justify-start gap-1 px-2 text-xs"
              onClick={() => onChange([])}
            >
              <X className="size-3" />
              Clear ({value.length})
            </Button>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Date range variant — two HTML date inputs in the popover with Apply /
// Clear. Stored as ISO date strings (YYYY-MM-DD) so they round-trip cleanly
// through URL params if a caller ever wants to persist them.
type DateRangeColumnFilterProps = {
  ariaLabel: string;
  value: { from?: string; to?: string };
  onChange: (next: { from?: string; to?: string }) => void;
};

export function DateRangeColumnFilter({ ariaLabel, value, onChange }: DateRangeColumnFilterProps) {
  const active = Boolean(value.from || value.to);
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(value.from ?? "");
  const [to, setTo] = useState(value.to ?? "");
  const fromId = useId();
  const toId = useId();
  // Re-seed the local draft whenever the dropdown opens so the form reflects
  // the current applied filter (not whatever the user typed last time).
  useEffect(() => {
    if (open) {
      setFrom(value.from ?? "");
      setTo(value.to ?? "");
    }
  }, [open, value.from, value.to]);

  function apply() {
    onChange({ from: from || undefined, to: to || undefined });
    setOpen(false);
  }

  function clear() {
    onChange({});
    setFrom("");
    setTo("");
    setOpen(false);
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={
            active ? `${ariaLabel}: ${value.from ?? "any"} to ${value.to ?? "any"}` : ariaLabel
          }
          aria-pressed={active}
          className="ml-1 inline-flex size-5 items-center justify-center rounded transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <FilterIconChip active={active} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={4}
        className="w-60 p-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-2">
          <div className="flex flex-col gap-1 text-xs">
            <label htmlFor={fromId}>From</label>
            <Input
              id={fromId}
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-8"
            />
          </div>
          <div className="flex flex-col gap-1 text-xs">
            <label htmlFor={toId}>To</label>
            <Input
              id={toId}
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-8"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" className="h-7 flex-1 text-xs" onClick={apply}>
              Apply
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={clear}
              disabled={!active && !from && !to}
            >
              Clear
            </Button>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
